import React, { useEffect, useMemo, useState } from 'react'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, IconButton, LinearProgress } from '@mui/material'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import Breadcrumb from '../../../components/Breadcrumb'
import { VIDEO, HOMEWORK } from '../../../utils/constants'
import VideoTab from './videoTab/VideoTab'
import HomeworkTab from './homeworkTab/HomeworkTab'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import { ui, tone } from '../../../utils/uiTokens'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import { H3, Body1 } from '../../../layouts/TextStyles'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CuratorStudentState } from '../../../features/types'
import { capitalize } from '../../../utils/functions'
import HistoryIcon from '@mui/icons-material/History';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { toast } from 'react-toastify'
import { useHeadScope } from '../../head/headScope'
import StudentControlBlock from './StudentControlBlock'
import StudentActivityBlock from './StudentActivityBlock'
import StudentAiMentorBlock from './StudentAiMentorBlock'

const StudentProgressPage = () => {
    const { formatMessage, locale } = useIntl()
    const { readOnly, director } = useHeadScope()
    const { state } = useLocation()
    const navigate = useNavigate()
    const { userCourseEnrollId } = useParams()
    // соседи из таблицы, откуда открыли карточку; открыли по прямой ссылке - кнопок нет
    const siblings: { id: number; to: string; student: object }[] = state?.siblings ?? []
    const position = siblings.findIndex((s) => s.id === +userCourseEnrollId)
    const goTo = (offset: number) => {
        const target = siblings[position + offset]
        if (target) navigate(target.to, { state: { student: target.student, siblings } })
    }
    const [subjectId, setSubjectId] = useState<number | null>(null)
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>('curator.monthNumber', undefined)
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>('curator.weekNumber', undefined)
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams('type=VIDEO'))
    const { data: studentProgress = {} as CuratorStudentState, isFetching: loadingStudentProgress, refetch: refetchStudentProgress } =
        curatorStudentsApiSlice.useFetchStudentProgressQuery({ userCourseEnrollId: +userCourseEnrollId })
    const [updateStudentPerformance, { isLoading: loadingUpdateStudentPerformance }] = curatorStudentsApiSlice.useUpdateStudentPerformanceMutation()
    const urlFullName = searchParams.get('fullName')
    const fullName = state?.student
        ? state.student.firstName + ' ' + state.student.lastName
        : urlFullName
            ? decodeURIComponent(urlFullName).replace(/-/g, ' ')
            : ''

    const handlePerformanceLevelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPerformanceLevel = e.target.value
        try {
            await updateStudentPerformance({ userCourseEnrollId: +userCourseEnrollId, performanceLevel: newPerformanceLevel }).unwrap()
            await refetchStudentProgress()
            toast.success(formatMessage({ id: 'performanceLevelUpdated' }))
        } catch (error) {
            console.error(error)
            toast.error(formatMessage({ id: 'error' }))
        }
    }

    const handleTabChange = (type: string) => {
        const p = new URLSearchParams(searchParams);
        p.set('type', type);
        setSearchParams(p, { replace: true });
    };

    const handleSubjectClick = (subjectId: number) => {
        setSubjectId(subjectId)
    }

    const getDuration = (daysLeft: number) => {
        if (daysLeft > 30) {
            return (
                <CourseInfoSpan color={ui.pink} backgroundColor={ui.pinkSoft}>
                    <Medium color={ui.pink}>
                        {Math.floor(daysLeft / 30) + ' ' + formatMessage({ id: 'month' })}
                    </Medium>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft % 30 + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>)
        } else if (daysLeft === 0) {
            return <FinishedLabel>✓ {formatMessage({ id: 'courseCompleted' })}</FinishedLabel>
        } else {
            return (
                <CourseInfoSpan color={ui.pink} backgroundColor={ui.pinkSoft}>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </CourseInfoSpan>
            )
        }
    }

    // Куратор ведёт не все предметы студента: показываем только свои.
    // Если бэкенд не отметил ни одного (нет привязки), падаем обратно на все предметы.
    const subjects = useMemo(() => {
        const allSubjects = studentProgress?.subjects || []
        const curatorSubjects = allSubjects.filter((subject) => subject.curatorSubject)

        return curatorSubjects.length ? curatorSubjects : allSubjects
    }, [studentProgress?.subjects])

    useEffect(() => {
        if (subjects.length && !subjectId) {
            const urlSubjectId = Number(searchParams.get('courseSubjectId'))
            const urlSubject = subjects.find(
                (subject) => subject.id === urlSubjectId,
            )

            setSubjectId(urlSubject ? urlSubject.id : subjects[0].id)
        }
    }, [subjects, subjectId, searchParams])


    return (
        <Wrapper>
            <Box sx={styles.boxContainer}>
                <Breadcrumb
                    links={[
                        formatMessage({ id: 'groups' }),
                        formatMessage({ id: 'student' }),
                        fullName
                    ]}
                />
                {position >= 0 && siblings.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginLeft: 'auto' }}>
                        <Button variant="outlined" size="small" disabled={position === 0} onClick={() => goTo(-1)}>
                            ← {formatMessage({ id: 'prevStudent' })}
                        </Button>
                        <Box sx={{ fontSize: '13px', color: ui.muted, minWidth: 56, textAlign: 'center' }}>
                            {position + 1} / {siblings.length}
                        </Box>
                        <Button variant="outlined" size="small" disabled={position === siblings.length - 1} onClick={() => goTo(1)}>
                            {formatMessage({ id: 'nextStudent' })} →
                        </Button>
                    </Box>
                )}
            </Box>

            {loadingStudentProgress ? (
                <LinearProgress />
            ) : (
                <Content>
                    <StudentCard>
                        <Box>
                            <Box sx={{ width: 140, height: 132, borderRadius: '24px', flexShrink: 0, background: ui.pinkSoft, color: ui.pink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PersonOutlineRoundedIcon sx={{ fontSize: 64 }} />
                            </Box>
                        </Box>
                        <Box sx={{ ...styles.studentInfo, marginLeft: '20px', marginRight: 'auto' }}>
                            <H3>{studentProgress?.firstName} {studentProgress?.lastName}</H3>
                            <Body1>{formatMessage({ id: 'birthDate' })}{' : '}<span style={{ fontWeight: 600, color: tone.ok.text }}>{studentProgress?.birthDate}</span></Body1>
                            {!readOnly && <Body1>{formatMessage({ id: 'mobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: tone.ok.text }}>{studentProgress?.mobile}</span></Body1>}
                            {!readOnly && <Body1>{formatMessage({ id: 'parentMobileNumber' })}{' : '}<span style={{ fontWeight: 600, color: tone.ok.text }}>{studentProgress?.parentMobile}</span></Body1>}
                        </Box>
                        <Box sx={{ ...styles.studentInfo, alignItems: 'flex-end' }}>
                            <Button variant="contained" color="primary" startIcon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} disabled>
                                {formatMessage({ id: 'note' })}
                            </Button>
                            <StatusSelect
                                value={studentProgress?.performanceLevel || formatMessage({ id: 'performance' })}
                                performanceLevel={studentProgress?.performanceLevel || ''}
                                onChange={handlePerformanceLevelChange}
                                disabled={readOnly || loadingUpdateStudentPerformance}>
                                <option disabled selected value={formatMessage({ id: 'performance' })}>{formatMessage({ id: 'performance' })}</option>
                                {studentProgress?.performances?.map((performance) => (
                                    <option key={performance} value={performance}>{formatMessage({ id: `${performance}` })}</option>
                                ))}
                            </StatusSelect>
                            <IconButton sx={styles.editIcon} disabled><EditOutlinedIcon /></IconButton>
                        </Box>
                    </StudentCard>
                    <CourseInfoCard>
                        <Box>
                            <Medium color={ui.ink}>{formatMessage({ id: 'courseInfo' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                <CourseInfoSpan color={tone.ok.text} backgroundColor={tone.ok.bg}>
                                    <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />
                                    {studentProgress?.[`coursesTitle${capitalize(locale)}`]}
                                </CourseInfoSpan>
                                {studentProgress?.daysLeft !== undefined && (
                                    getDuration(studentProgress?.daysLeft)
                                )}

                            </Box>
                        </Box>
                        <Box>
                            <Medium color={ui.ink}>{formatMessage({ id: 'subjects' })}</Medium>
                            <Box sx={styles.courseInfoBox}>
                                {subjects.map((subject) => (
                                    <Chip key={subject.id}
                                        label={subject?.[`title${capitalize(locale)}`]}
                                        sx={{
                                            cursor: 'pointer',
                                            fontWeight: subjectId === subject.id ? 600 : 400,
                                            backgroundColor: subjectId === subject.id ? ui.pinkSoft : 'transparent',
                                            border: `1px solid ${subjectId === subject.id ? 'transparent' : ui.border}`,
                                            color: subjectId === subject.id ? ui.pink : ui.sub,
                                            '&:hover': { backgroundColor: ui.pinkSoft, color: ui.pink },
                                        }}
                                        onClick={() => handleSubjectClick(subject.id)}
                                    />
                                ))}
                            </Box>
                        </Box>

                    </CourseInfoCard>

                    <StudentControlBlock userCourseEnrollId={+userCourseEnrollId} />

                    {/* ментору и пән басшысына не нужно: время входов и так видно под видеоуроками */}
                    {director && <StudentActivityBlock userCourseEnrollId={+userCourseEnrollId} />}

                    {director && <StudentAiMentorBlock userCourseEnrollId={+userCourseEnrollId} />}

                    <Box sx={{ flexDirection: 'column', display: 'flex', gridColumn: '1 / -1', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            {/* Фильтр только у вкладок с процентами: домашка и видеозаметки не по темам */}
                            {(searchParams.get('type') || VIDEO) === VIDEO ? (
                                <MonthWeekFilter
                                    courseSubjectId={subjectId ?? undefined}
                                    monthNumber={monthNumber}
                                    weekNumber={weekNumber}
                                    onChange={(month, week) => {
                                        setMonthNumber(month)
                                        setWeekNumber(week)
                                    }}
                                />
                            ) : (
                                <Box />
                            )}

                        <Tabs>
                            <Tab
                                searchParams={searchParams.get('type')}
                                type={VIDEO}
                                onClick={() => handleTabChange(VIDEO)}
                            >
                                {formatMessage({ id: 'video' })}
                            </Tab>

                            <Tab
                                searchParams={searchParams.get('type')}
                                type={HOMEWORK}
                                onClick={() => handleTabChange(HOMEWORK)}
                            >
                                {formatMessage({ id: 'homework' })}
                            </Tab>
                        </Tabs>
                        </Box>
                        {searchParams.get('type') === VIDEO && studentProgress?.userId && (
                            <VideoTab
                                studentId={+studentProgress?.userId}
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                                monthNumber={monthNumber}
                                weekNumber={weekNumber}
                            />
                        )}
                        {searchParams.get('type') === HOMEWORK && (
                            <HomeworkTab
                                userCourseEnrollId={+userCourseEnrollId}
                                subjectId={subjectId}
                                loadingSubjects={loadingStudentProgress}
                            />
                        )}
                    </Box>
                </Content>
            )}
        </Wrapper>
    )
}

export default StudentProgressPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: max-content max-content 1fr;
    gap: 16px;
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;

    @media screen and (max-width: 900px) {
        grid-template-columns: 1fr;
        grid-template-rows: repeat(3, max-content) 1fr;
    }
`

// Сегментированный переключатель, как пилюли недель в фильтре
const Tabs = styled.div`
    margin-left: auto;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
    cursor: pointer;
`

const Tab = styled.div<{ searchParams: string; type: string }>`
    ${(props) => {
        const active =
            props.searchParams == props.type ||
            (props.type == VIDEO && props.searchParams == null)

        return `
            background: ${active ? ui.pinkSoft : 'transparent'};
            border: 1px solid ${active ? 'transparent' : ui.border};
            color: ${active ? ui.pink : ui.sub};
            font-weight: ${active ? 600 : 400};
        `
    }}
    border-radius: 12px;
    padding: 8px 16px;
    font-size: 13.5px;
    line-height: 1.2;
    white-space: nowrap;

    &:hover {
        border-color: ${ui.pink};
        color: ${ui.pink};
    }
`

const StudentCard = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;

    @media screen and (max-width: 600px) {
        flex-wrap: wrap;
        gap: 16px;

        /* height: 100% у колонок при переносе тянет их на всю карточку */
        && > div {
            height: auto;
        }
    }

    padding: 20px;
    border-radius: 16px;
    border: 1px solid ${ui.border};
    background-color: ${ui.card};
`

const CourseInfoCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    border-radius: 16px;
    border: 1px solid ${ui.border};
    background-color: ${ui.card};
`

const CourseInfoSpan = styled.span<{ color: string, backgroundColor: string }>`
    color: ${({ color }) => color};
    background-color: ${({ backgroundColor }) => backgroundColor};
    padding: 6px 12px;
    border-radius: 24px;
    font-weight: 600;
    font-size: 16px;
    display: flex;
    text-align: center;
    gap: 8px;
`

const StatusSelect = styled.select<{ performanceLevel: string }>`
    background-color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return tone.ok.bg;
            case 'MEDIUM':
                return tone.info.bg;
            case 'LOW':
                return tone.warn.bg;
            case 'NEEDS_HELP':
                return tone.bad.bg;
            default:
                return ui.track;
        }
    }};
    color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return tone.ok.text;
            case 'MEDIUM':
                return tone.info.text;
            case 'LOW':
                return tone.warn.text;
            case 'NEEDS_HELP':
                return tone.bad.text;
            default:
                return ui.ink;
        }
    }};
    border-radius: 100px;
    padding: 6px 0;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
    border: none;
    outline: none;
    cursor: pointer;
`

const Medium = styled.span<{ color: string }>`
    font-size: 16px;
    font-weight: 600;
    color: ${({ color }) => color};
`

const DayLabel = styled.span`
    font-size: 14px;
    color: ${ui.sub};
    display: flex;
    align-items: center;
    gap: 2px;
`

const FinishedLabel = styled.span`
    color: ${tone.ok.text};
    font-weight: 500;
    font-size: 16px;
    background-color: ${tone.ok.border};
    border: 4px solid ${tone.ok.border};
    border-radius: 100px;
    padding: 2px 12px;
`

const styles = {
    boxContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'space-between',
        mb: 1,
    },
    studentInfo: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    courseInfoBox: {
        marginTop: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    editIcon: {
        background: ui.pinkSoft,
        borderRadius: '10px',
        color: ui.pink,
        width: '42px',
        height: '42px',
    },
}